import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './PortfolioSummary.css';
import { API_BASE_URL } from '../../../utils/constants';
import { useCurrency } from '../../../context/CurrencyContext';

const PortfolioSummary = ({ holdings }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { currency } = useCurrency();
  const navigate = useNavigate();

  const formatQuantity = (quantity) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 6
    }).format(quantity);
  };

  // Use this function to format currency - it will use the _display field if available
  const getCurrencyDisplay = (holding, field) => {
    const displayField = `${field}_display`;
    // If backend provides a formatted display value, use it
    if (holding[displayField]) {
      return holding[displayField];
    }
    // Otherwise, format the raw value based on current currency
    const rawValue = holding[field] || 0;
    const numValue = parseFloat(rawValue);
    
    if (currency === 'NGN') {
      return new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: 'NGN',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numValue);
    } else {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numValue);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    
    const baseUrl = API_BASE_URL.replace('/api', '');
    const cleanPath = imagePath.startsWith('/') ? imagePath.slice(1) : imagePath;
    
    return `${baseUrl}/${cleanPath}`;
  };

  const nextSlide = useCallback(() => {
    if (!holdings || holdings.length <= 1) return;
    setCurrentIndex((prev) => (prev + 1) % holdings.length);
  }, [holdings]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  // Auto-play functionality
  useEffect(() => {
    if (!holdings || holdings.length <= 1) return;

    const interval = setInterval(() => {
      nextSlide();
    }, 10000); // 10 seconds

    return () => clearInterval(interval);
  }, [nextSlide, holdings]);

  const handleNavigateToStocks = () => {
    navigate('/stocks');
  };

  if (!holdings || holdings.length === 0) {
    return (
      <div className="portfolio-summary">
        <h2>Portfolio</h2>
        <div className="no-holdings" onClick={handleNavigateToStocks}>
          <div className="no-holdings-icon">
            <svg 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              className="portfolio-icon"
            >
              <path 
                d="M20 7H16V5C16 3.9 15.1 3 14 3H10C8.9 3 8 3.9 8 5V7H4C2.9 7 2 7.9 2 9V20C2 21.1 2.9 22 4 22H20C21.1 22 22 21.1 22 20V9C22 7.9 21.1 7 20 7ZM10 5H14V7H10V5ZM20 20H4V9H8V11H10V9H14V11H16V9H20V20Z" 
                fill="currentColor"
              />
              <path 
                d="M12 12C10.9 12 10 12.9 10 14C10 15.1 10.9 16 12 16C13.1 16 14 15.1 14 14C14 12.9 13.1 12 12 12Z" 
                fill="currentColor"
              />
            </svg>
          </div>
          <div className="no-holdings-content">
            <h3>Start Your Investment Journey</h3>
            <p>Explore and invest in local companies to grow your wealth and build a diversified portfolio</p>
            <span className="browse-stocks-link">Browse Available Stocks →</span>
          </div>
        </div>
      </div>
    );
  }

  // Render a single holding card
  const renderHoldingCard = (holding, index) => {
    const imageUrl = getImageUrl(holding.stock?.image);
    const profitLoss = parseFloat(holding.profit_loss) || 0;
    const profitLossPercentage = parseFloat(holding.profit_loss_percentage) || 0;
    
    return (
      <div className="holding-item" key={holding.id || index}>
        <div className="holding-main">
          <div className="stock-info">
            <div className="stock-symbol">{holding.stock?.symbol}</div>
            <div className="stock-name">{holding.stock?.name}</div>
            <div className="stock-image-container">
              {imageUrl ? (
                <img 
                  src={imageUrl} 
                  alt={holding.stock?.symbol}
                  className="stock-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `<span class="stock-image-placeholder">${holding.stock?.symbol?.charAt(0) || '?'}</span>`;
                  }}
                />
              ) : (
                <span className="stock-image-placeholder">
                  {holding.stock?.symbol?.charAt(0) || '?'}
                </span>
              )}
            </div>
          </div>
          <div className="holding-details">
            <div className="quantity">{formatQuantity(holding.quantity)} shares</div>
            <div className="values">
              <div className="current-value">
                {getCurrencyDisplay(holding, 'current_value')}
              </div>
              <div className="price-per-share">
                @ {getCurrencyDisplay(holding, 'average_buy_price')}
              </div>
            </div>
          </div>
        </div>
        
        {(holding.profit_loss !== undefined || holding.profit_loss_percentage !== undefined) && (
          <div className={`performance ${profitLoss >= 0 ? 'positive' : 'negative'}`}>
            <div className="profit-loss">
              {getCurrencyDisplay(holding, 'profit_loss')}
            </div>
            <div className="profit-loss-percentage">
              {profitLossPercentage >= 0 ? '+' : ''}
              {profitLossPercentage.toFixed(2)}%
            </div>
          </div>
        )}
      </div>
    );
  };

  // If only one holding, show it without carousel controls
  if (holdings.length === 1) {
    return (
      <div className="portfolio-summary">
        <h2>Your Portfolio</h2>
        <div className="portfolio-container single-holding">
          {renderHoldingCard(holdings[0], 0)}
        </div>
      </div>
    );
  }

  return (
    <div className="portfolio-summary">
      <div className="portfolio-header">
        <h2>Your Portfolio</h2>
        <div className="portfolio-counter">
          {currentIndex + 1} / {holdings.length}
        </div>
      </div>

      <div className="portfolio-carousel">
        <div className="carousel-track">
          <div 
            className="carousel-slides"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {holdings.map((holding, index) => (
              <div key={holding.id || index} className="carousel-slide">
                {renderHoldingCard(holding, index)}
              </div>
            ))}
          </div>
        </div>

        {/* Dot Navigation */}
        <div className="carousel-dots">
          {holdings.map((_, index) => (
            <button
              key={index}
              className={`dot ${index === currentIndex ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to holding ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PortfolioSummary;